import { useEffect, useRef, type RefObject } from 'react';

/**
 * Proof line under the home hero buttons (layout report moves #3 and #16):
 * one quiet line of plain facts, no badges, no boxes. Separators are drawn in
 * CSS and clipped at the start of each wrapped line, so a phone never shows a
 * line that begins with a dot.
 *
 * Sources (all already stated in the repo):
 *  - 1997: src/data/site.ts foundingYear, HomeIntro eyebrow
 *  - HOUR Detroit since 2012, Oakland County since 1999: Dr. Anna's bio in
 *    src/data/site.ts (the home stats box that repeated them was removed
 *    09-29; this line now carries them)
 *  - 5.0 on Google: listing rating captured in functions/data/reviews-harvest.json
 *    (rating 5.0, 463 reviews, harvested 2026-08-26); the live /api/reviews
 *    also returns 5. Static on purpose, so the hero never shifts while the
 *    live data loads.
 */
// ASK Zach: confirm these numbers
const PROOF_ITEMS: ReadonlyArray<{ text: string; star?: boolean; srText?: string }> = [
  { text: 'Serving Royal Oak families since 1997' },
  { text: 'HOUR Detroit Top Chiropractor since 2012' },
  { text: "Oakland County's Best since 1999" },
  { text: '5.0 on Google', star: true, srText: 'Rated 5.0 out of 5 on Google' },
];

/**
 * Phones: the fixed accessibility button owns the bottom-left corner (44px
 * at 13-18px from the edges, 64px hit area). On short screens (360x740,
 * 375x667 in Safari, 768x1024) the proof line sits in that corner in the
 * first screen. While any proof item does, <html> carries .ve-floats-held and
 * chrome.css slides the accessibility button (and the rating badge, which is
 * not shown this high anyway) below the screen edge; they come back as soon
 * as the line has scrolled clear, and past the first screen they never hold.
 * Keyboard focus still reaches them (chrome.css un-holds on focus).
 *
 * The chat bubble is never held (QA 09-29: a held bubble could not be tapped
 * until the visitor scrolled). The proof items are left-aligned and end
 * short of its corner: measured 09-30 at load, no item sits under the bubble
 * at 320x568, 360x740, 375x667, 390x844, 412x915 or 768x1024 (Chromium and
 * WebKit), and the bubble answers a tap on the first screen.
 */
const LEFT_ZONE_W = 72;
const FLOAT_ZONE_PX = 84;
const FIRST_SCREEN_SCROLL_PX = 240;
const HELD_CLASS = 've-floats-held';

function useHoldFloatersOver(target: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = target.current;
    if (!el || typeof window.matchMedia !== 'function') return;
    const phone = window.matchMedia('(max-width: 768px)');
    const root = document.documentElement;
    let frame = 0;
    const update = () => {
      frame = 0;
      const zoneTop = window.innerHeight - FLOAT_ZONE_PX;
      const inZone = Array.from(el.children).some((item) => {
        const box = item.getBoundingClientRect();
        return box.left < LEFT_ZONE_W && box.bottom > zoneTop && box.top < window.innerHeight;
      });
      root.classList.toggle(HELD_CLASS, phone.matches && window.scrollY < FIRST_SCREEN_SCROLL_PX && inZone);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    phone.addEventListener?.('change', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      phone.removeEventListener?.('change', schedule);
      root.classList.remove(HELD_CLASS);
    };
  }, [target]);
}

export default function ProofLine() {
  const listRef = useRef<HTMLUListElement>(null);
  useHoldFloatersOver(listRef);

  return (
    <div className="ve-home-proof">
      <ul ref={listRef} className="ve-home-proof-list" aria-label="Why Royal Oak families trust us">
        {PROOF_ITEMS.map((item) => (
          <li key={item.text}>
            {item.star ? (
              <>
                <span className="ve-home-proof-star" aria-hidden="true">
                  ★
                </span>
                <span aria-hidden="true">{item.text}</span>
                <span className="sr-only">{item.srText ?? item.text}</span>
              </>
            ) : (
              item.text
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
